import { useState, useRef, useEffect, useCallback } from "react";
import { aiChatBotTVS3 } from "../services/apiService";
import { v4 as uuidv4 } from 'uuid';
import VoiceRecorder, { VoiceRecorderHandle } from "../components/VoiceRecorder2";
import AvironixVoiceRecorder, { AvironixVoiceRecorderHandle } from "../components/AvironixVoiceRecorder";
import Chart from 'chart.js/auto';
import xoroLogo from "../assects/img/SUN TWS Round.png";
import { useAuth } from '../hooks/AuthContext';

interface Message {
  id: string;
  sender: "user" | "bot" | "loading";
  text: string;
  chartData?: ChartData;
  messageType?: "text" | "chart" | "mixed";
}

interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'doughnut';
  data: {
    labels: string[];
    datasets: {
      label?: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string | string[];
      borderWidth?: number;
    }[];
  };
  options?: any;
  title?: string;
}

const ChatChart = ({ chartData, messageId }: { chartData: ChartData; messageId: string }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (chartRef.current && chartData) {
      if (chartInstanceRef.current) chartInstanceRef.current.destroy();

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, chartRef.current.width, chartRef.current.height);
        chartInstanceRef.current = new Chart(ctx, {
          type: chartData.type,
          data: chartData.data,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { intersect: false, mode: 'index' },
            layout: { padding: 0 },
            elements: { bar: { borderRadius: 4, borderSkipped: false, borderWidth: 0 } },
            plugins: {
              title: {
                display: !!chartData.title,
                text: chartData.title,
                font: { size: 16, weight: 'bold' },
                color: '#374151',
                padding: { bottom: 20 }
              },
              legend: {
                position: 'bottom' as const,
                labels: { color: '#374151', font: { size: 12 }, padding: 15, usePointStyle: true }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#374151',
                borderWidth: 1,
                cornerRadius: 8,
                callbacks: {
                  label: function (context: any) {
                    const value = context.parsed.y || context.parsed;
                    if (typeof value === 'number') {
                      if (value >= 1000000) return context.dataset.label + ': ₹' + (value / 1000000).toFixed(1) + 'M';
                      if (value >= 1000) return context.dataset.label + ': ₹' + (value / 1000).toFixed(1) + 'K';
                      return context.dataset.label + ': ₹' + value.toLocaleString();
                    }
                    return context.dataset.label + ': ' + value;
                  }
                }
              }
            },
            scales: chartData.type !== 'pie' && chartData.type !== 'doughnut' ? {
              y: {
                beginAtZero: true,
                border: { display: false, width: 0 },
                ticks: {
                  color: '#6B7280',
                  font: { size: 11 },
                  padding: 8,
                  callback: function (value: any) {
                    const numValue = Number(value);
                    if (numValue >= 1000000) return '₹' + (numValue / 1000000).toFixed(1) + 'M';
                    if (numValue >= 1000) return '₹' + (numValue / 1000).toFixed(0) + 'K';
                    return '₹' + numValue.toLocaleString();
                  }
                },
                grid: { color: 'transparent', lineWidth: 0, drawBorder: false, drawOnChartArea: false, drawTicks: false }
              },
              x: {
                border: { display: false, width: 0 },
                ticks: {
                  color: '#6B7280',
                  font: { size: 10 },
                  maxRotation: 45,
                  minRotation: 0,
                  padding: 8,
                  callback: function (value: any) {
                    // @ts-ignore - ChartJS typing quirk
                    const label = this.getLabelForValue(Number(value));
                    return label && label.length > 15 ? label.substring(0, 12) + '...' : label;
                  }
                },
                grid: { display: false, drawBorder: false, drawOnChartArea: false, drawTicks: false, lineWidth: 0 }
              }
            } : {},
            ...chartData.options
          }
        });
      }
    }
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, messageId]);

  const handleDownload = () => {
    const instance = chartInstanceRef.current;
    if (!instance) return;

    const canvas = instance.canvas as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Create a temporary canvas with same size
    const tmpCanvas = document.createElement("canvas");
    tmpCanvas.width = canvas.width;
    tmpCanvas.height = canvas.height;
    const tmpCtx = tmpCanvas.getContext("2d");

    if (tmpCtx) {
      // Fill white background
      tmpCtx.fillStyle = "#ffffff";
      tmpCtx.fillRect(0, 0, tmpCanvas.width, tmpCanvas.height);

      // Draw chart canvas over white background
      tmpCtx.drawImage(canvas, 0, 0);

      // Export with background
      const dataUrl = tmpCanvas.toDataURL("image/png", 1.0);

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `chart-${messageId}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };


  return (
    <div className="w-full mt-2">
      <div className="relative w-full h-80 bg-transparent rounded-xl overflow-hidden">
        <canvas
          ref={chartRef}
          className="w-full h-full bg-transparent"
          style={{ background: 'transparent' }}
        />
        <button
          type="button"
          onClick={handleDownload}
          className="absolute top-2 right-2 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-white/90 border border-gray-200 hover:bg-white shadow-sm transition"
          aria-label="Download chart image"
          title="Download chart image"
        >
          Download
        </button>
      </div>
    </div>
  );
};

function TvsChatInterface3() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [wholeLoading, setWholeLoading] = useState(false);
  const [current_user_chat_code, setCurrent_user_chat_code] = useState("");
  const [audioLoading, setAudioLoading] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const voiceRecorderRef = useRef<AvironixVoiceRecorderHandle>(null);

  const { logout } = useAuth();

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const parseChartData = useCallback((response: string): { text: string; chartData?: ChartData } => {
    try {
      const chartMatch = response.match(/\[CHART_DATA\]([\s\S]*?)\[\/CHART_DATA\]/);
      if (chartMatch) {
        const chartDataStr = chartMatch[1];
        const chartData = JSON.parse(chartDataStr);
        const textContent = response.replace(/\[CHART_DATA\][\s\S]*?\[\/CHART_DATA\]/, '').trim();
        return { text: textContent, chartData };
      }
    } catch (error) {
      console.error('Error parsing chart data:', error);
    }
    return { text: response };
  }, []);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const handleBotReply = useCallback(async (userInput: string, cc?: string, audioBlob?: Blob) => {
    try {
      setLoading(true);

      const formData = new FormData();
      let audio = false;

      voiceRecorderRef.current?.stopRecording();

      if (audioBlob) {
        formData.append('audio', audioBlob, 'voice.webm');
        formData.append('userQuery', "");
        setMessages(prev => [...prev, { 
          id: uuidv4(),
          sender: "user", 
          text: "🎤 (voice message sent)", 
          messageType: "text" 
        }]);
        setMessages(prev => [...prev, { 
          id: uuidv4(),
          sender: "loading", 
          text: "🤖 Getting response...", 
          messageType: "text" 
        }]);
        audio = true;
      } else {
        formData.append('userQuery', userInput);
        if(userInput !== "SUBS123"){
            const userMessage: Message = { 
              id: uuidv4(),
              sender: "user", 
              text: userInput, 
              messageType: "text" 
            };
            setMessages((prev) => [...prev, userMessage]);
        }
        
        setMessages((prev) => [...prev, { 
          id: uuidv4(),
          sender: "loading", 
          text: "🤖 Getting response...", 
          messageType: "text" 
        }]);
      }

      formData.append('current_user_chat_code', current_user_chat_code || cc || "");

      const response = await aiChatBotTVS3(formData);
      const userAudioText = response.transcribed_text;
      let botReply = response.reply || "No response";

      if (audio && userAudioText) {
        setMessages((prev: Message[]) => {
          return prev.map((msg: Message, index: number) => {
            if (index === prev.length - 2) {
              return { ...msg, text: `🎤 (voice message sent) - ${userAudioText}` };
            }
            return msg;
          });
        });
      }

      const { text, chartData } = parseChartData(botReply);

      const finalMessage: Message = {
        id: uuidv4(),
        sender: "bot",
        text: text,
        chartData: chartData,
        messageType: chartData ? "mixed" : "text"
      };

      setMessages((prev) => {
        const filtered = prev.filter(msg => msg.text !== "🤖 Getting response...");
        return [...filtered, finalMessage];
      });

      if (response.role && response.role !== "") {
        localStorage.setItem('userBotRole', response.role);
      }
    } catch (error) {
      console.error('Error in handleBotReply:', error);
      setMessages((prev) => {
        const filtered = prev.filter(msg => msg.text !== "🤖 Getting response...");
        return [...filtered, { 
          id: uuidv4(),
          sender: "bot", 
          text: "Sorry, something went wrong.", 
          messageType: "text" 
        }];
      });
    } finally {
      setLoading(false);
    }
  }, [current_user_chat_code, parseChartData]);

  const sendMessage = useCallback(async (audioBlob?: Blob) => {
    const currentInput = input;
    setInput("");
    await handleBotReply(currentInput, "", audioBlob);
  }, [input, handleBotReply]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      sendMessage();
    }
  }, [sendMessage]);

  useEffect(() => {
    const CC = uuidv4();
    setCurrent_user_chat_code(CC);
    
    const init = async () => {
      setWholeLoading(true);
      try {
        const formData = new FormData();
        formData.append('userQuery', "SUBS123");
        formData.append('current_user_chat_code', CC);

        const response = await aiChatBotTVS3(formData);
        const botReply = response.reply || "No response";

        const finalMessage: Message = {
          id: uuidv4(),
          sender: "bot",
          text: botReply,
          messageType: "text"
        };

        setMessages([finalMessage]);

        if (response.role && response.role !== "") {
          localStorage.setItem('userBotRole', response.role);
        }
      } catch (error) {
        console.error('Error during initialization:', error);
      } finally {
        setWholeLoading(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  return (
    <div className="w-full h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      
      <div className="relative bg-white/80 backdrop-blur-xl border-b border-gray-200/60 px-3 sm:px-6 py-4 sm:py-5">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-2.5 bg-white rounded-xl shadow-lg border border-gray-200/50">
              <img 
                src={xoroLogo} 
                alt="XORO Story Logo" 
                className="w-5 h-5 sm:w-6 sm:h-6 object-cover rounded-full"
              />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold">
                <span className="text-gray-800">SUN TWS </span>
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent font-bold">Assistant</span>
              </h1>
            </div>
          </div>
          
          <button 
            onClick={handleLogout}
            className="p-2 sm:p-2.5 bg-gray-500 hover:bg-gray-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2"
            type="button"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="hidden sm:inline text-white text-sm font-medium">Logout</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-1 sm:px-2 py-4 sm:py-6">
        <div className="w-full sm:w-[90%] lg:w-[80%] mx-auto space-y-4 sm:space-y-6">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex items-start ${
                msg.sender === "user" 
                ? "flex-row-reverse"
                : "space-x-2 sm:space-x-3"
              }`}
            >
              <div className={`flex-shrink-0 w-7 h-7 sm:w-9 sm:h-9 flex items-center justify-center ${
                msg.sender === "user" 
                  ? "rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-md ml-3 sm:ml-4" 
                  : ""
              }`}>
                {msg.sender === "user" ? (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                ) : (
                  <img 
                    src={xoroLogo} 
                    alt="XORO Logo" 
                    className="w-5 h-5 sm:w-6 sm:h-6 object-cover rounded-full border border-gray"
                  />
                )}
              </div>

              <div className={`max-w-[85%] sm:max-w-[75%] ${msg.sender === "user" ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`px-3 sm:px-4 py-2 sm:py-3 rounded-2xl shadow-sm border text-sm sm:text-base ${
                  msg.sender === "user"
                    ? "bg-gradient-to-br from-blue-500 to-purple-600 text-white border-transparent"
                    : msg.sender === "loading"
                    ? "bg-white/90 backdrop-blur-sm border-gray-200/50 text-gray-600"
                    : "bg-white/90 backdrop-blur-sm border-gray-200/50 text-gray-800"
                } ${msg.sender !== "user" ? "rounded-tl-md" : "rounded-tr-md"}`}>
                  
                  {msg.text === "🤖 Getting response..." ? (
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                      <span className="text-xs sm:text-sm">Getting response...</span>
                    </div>
                  ) : (
                    <div dangerouslySetInnerHTML={{
                      __html: msg.text,
                    }} />
                  )}
                </div>
                
                {msg.chartData && (
                  <ChatChart chartData={msg.chartData} messageId={msg.id} />
                )}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
      </div>

      <div className="p-3 sm:p-4 bg-white/60 backdrop-blur-xl border-t border-gray-200/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end space-x-2 sm:space-x-3 w-full">
            <div className="flex-1 relative">
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg hover:shadow-xl focus-within:shadow-xl focus-within:border-blue-300/50 transition-all duration-300">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="What's in your mind?..."
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-transparent border-none focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-base"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-2">
              <AvironixVoiceRecorder
                ref={voiceRecorderRef}
                onAudioRecorded={(audioBlob: Blob) => sendMessage(audioBlob)}
                audioLoading1={(status: boolean) => setAudioLoading(status)}
                disabled={loading}
              />
              
              {!audioLoading &&
              <button 
                onClick={() => sendMessage()} 
                disabled={loading || !input.trim()}
                className="p-3 lg:p-4 bg-gradient-to-br from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 rounded-2xl shadow-lg hover:shadow-xl disabled:shadow-md transition-all duration-300 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
                type="button"
              >
                {loading ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                  </svg>
                )}
              </button>
            }
            </div>
          </div>
        </div>
      </div>

      {wholeLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-2xl p-6 flex flex-col items-center space-y-4 max-w-sm w-full">
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
            <p className="text-gray-700 font-medium text-center text-sm sm:text-base">Preparing your chat please wait...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default TvsChatInterface3;